import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiMapPin,
  FiPackage,
  FiXCircle,
  FiImage,
  FiVideo,
} from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";

const getStatusBadge = (status) => {
  switch (status) {
    case "Approved":
      return {
        label: "Approved",
        bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    case "Rejected":
      return {
        label: "Rejected",
        bg: "bg-red-50 text-red-700 border-red-200",
      };
    case "Offer_Created":
      return {
        label: "Offer Created",
        bg: "bg-blue-50 text-blue-700 border-blue-200",
      };
    case "Expired":
      return {
        label: "Expired",
        bg: "bg-orange-50 text-orange-700 border-orange-200",
      };
    case "Cancelled":
      return {
        label: "Cancelled",
        bg: "bg-gray-50 text-gray-700 border-gray-200",
      };
    default:
      return {
        label: "Under Review",
        bg: "bg-amber-50 text-amber-700 border-amber-200",
      };
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    return "—";
  }
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const SupplierRQMDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rqmId = searchParams.get("rqmId");

  const [rqm, setRqm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMedia, setActiveMedia] = useState(null);

  const fetchDetails = async () => {
    if (!rqmId) return;
    try {
      setLoading(true);
      const res = await axios.get(`/rqm/${rqmId}`);
      if (res?.data?.success) {
        setRqm(res.data.data);
      } else {
        toast.error("RQM request details not found");
      }
    } catch (err) {
      console.error("Failed to fetch RQM details:", err);
      toast.error(err?.response?.data?.message || "Failed to load RQM details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [rqmId]);

  if (loading) {
    return <AppLoader fullScreen message="Loading RQM request details..." />;
  }

  if (!rqm) {
    return (
      <div className="max-w-4xl mx-auto my-12 p-8 bg-white border rounded-2xl text-center space-y-4">
        <FiXCircle size={48} className="mx-auto text-red-500" />
        <h2 className="text-xl font-bold text-gray-800">RQM Request Not Found</h2>
        <p className="text-sm text-gray-500">The requested RQM details could not be loaded or do not exist.</p>
        <button
          onClick={() => navigate("/supplier-rqm-requests")}
          className="inline-flex items-center gap-2 bg-primaryColor text-white text-xs font-semibold px-4 py-2 rounded-xl"
        >
          <FiArrowLeft size={16} /> Back to My RQM Requests
        </button>
      </div>
    );
  }

  const badge = getStatusBadge(rqm.status);
  const categoryName = rqm.category?.name || "Category N/A";
  const subCategoryName = rqm.subCategory?.name || "SubCategory N/A";
  const productName = rqm.product?.name || "Product N/A";
  const varietyName = rqm.productVariety?.varietyName || rqm.customVarietyName || "Variety N/A";
  const unit = rqm.priceAndQuantity?.standardizedUnit || "Kg";
  const images = rqm.referencePhotoAndVideo?.images || [];
  const videos = rqm.referencePhotoAndVideo?.videos || [];

  return (
    <div className="w-full bg-[#F8FAFC] min-h-screen py-8 px-4 md:px-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* HEADER BAR */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/supplier-rqm-requests")}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
            >
              <FiArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-extrabold text-gray-900 font-mono">
                  {rqm.rqmNumber || "RQM"}
                </h1>
                <span className={`text-xs font-bold px-3 py-0.5 rounded-full border ${badge.bg}`}>
                  {badge.label}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Submitted on {formatDate(rqm.timeline?.submittedAt || rqm.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {/* ADMIN REVIEW / STATUS BANNER */}
        {rqm.status === "Rejected" && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-2xl flex items-start gap-3 text-red-800 text-xs">
            <FiXCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">RQM Request Rejected</p>
              <p className="mt-1">{rqm.adminReview?.reviewNote || "No rejection reason provided."}</p>
            </div>
          </div>
        )}

        {rqm.status === "Offer_Created" && (
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl flex items-start gap-3 text-blue-800 text-xs">
            <FiCheckCircle size={20} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Offer Created from RQM</p>
              <p className="mt-0.5">
                Offer Unique Number: <span className="font-mono font-bold">{rqm.linkedOffer?.offerId?.offerUniqueNumber || "OFR"}</span>
              </p>
            </div>
          </div>
        )}

        {/* MAIN DETAILS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* LEFT 2 COLUMNS - PRODUCT & PRICING */}
          <div className="md:col-span-2 space-y-6">

            {/* PRODUCT CARD */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 text-primaryColor font-bold text-sm border-b border-gray-100 pb-3">
                <FiBox size={18} /> Product & Variety Information
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Category</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{categoryName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Sub-Category</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{subCategoryName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Product</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{productName}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Variety Name</p>
                  <p className="font-bold text-primaryColor mt-0.5 text-sm">{varietyName}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Unit</p>
                  <p className="font-semibold text-gray-800 mt-0.5">{unit}</p>
                </div>
              </div>
            </div>

            {/* PRICING & QUANTITY CARD */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 text-primaryColor font-bold text-sm border-b border-gray-100 pb-3">
                <FiFileText size={18} /> Quantity & Pricing Breakdown
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Offered Quantity</p>
                  <p className="text-base font-extrabold text-gray-900 mt-1">
                    {rqm.priceAndQuantity?.quantity || 0} {unit}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Rate per {unit}</p>
                  <p className="text-base font-extrabold text-emerald-600 mt-1">
                    {formatCurrency(rqm.pricing?.amount)}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 col-span-2 sm:col-span-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Total Deal Value</p>
                  <p className="text-base font-extrabold text-primaryColor mt-1">
                    {formatCurrency(rqm.priceAndQuantity?.totalDealValue)}
                  </p>
                </div>
              </div>
            </div>

            {/* PACK SIZE & HOLDING PERIOD */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 text-primaryColor font-bold text-sm border-b border-gray-100 pb-3">
                <FiPackage size={18} /> Packaging & Holding Specifications
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Pack Size Weight</p>
                  <p className="font-semibold text-gray-800">
                    {rqm.packSize?.weight || 1} {rqm.packSize?.unit || unit} per pack
                  </p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mt-2">Dimensions (L x B x H)</p>
                  <p className="font-semibold text-gray-800">
                    {rqm.packSize?.dimensions?.length || 0} x {rqm.packSize?.dimensions?.breadth || 0} x{" "}
                    {rqm.packSize?.dimensions?.height || 0} {rqm.packSize?.dimensions?.measured || "cm"}
                  </p>
                </div>

                <div className="space-y-1 border-t sm:border-t-0 sm:border-l border-gray-100 sm:pl-4 pt-3 sm:pt-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                    <FiCalendar size={12} /> Holding Period From
                  </p>
                  <p className="font-semibold text-gray-800">{formatDate(rqm.holdingPeriod?.from)}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1 mt-2">
                    <FiClock size={12} /> Holding Period To
                  </p>
                  <p className="font-semibold text-gray-800">{formatDate(rqm.holdingPeriod?.to)}</p>
                </div>
              </div>
            </div>

          </div>

          {/* RIGHT 1 COLUMN - ADDRESS & MEDIA */}
          <div className="space-y-6">

            {/* PICKUP ADDRESS */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 text-primaryColor font-bold text-sm border-b border-gray-100 pb-3">
                <FiMapPin size={18} /> Pickup Address
              </div>

              {rqm.pickUpAddress ? (
                <div className="text-xs space-y-1 text-gray-700">
                  <span className="inline-block px-2 py-0.5 rounded bg-gray-100 font-bold text-[10px] text-gray-600 mb-1">
                    {rqm.pickUpAddress.addressType || "Office"}
                  </span>
                  <p className="font-semibold text-gray-900">{rqm.pickUpAddress.addressLine1}</p>
                  {rqm.pickUpAddress.addressLine2 && <p>{rqm.pickUpAddress.addressLine2}</p>}
                  <p>
                    {rqm.pickUpAddress.city}, {rqm.pickUpAddress.state} - {rqm.pickUpAddress.pincode}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-400">No pickup address attached.</p>
              )}
            </div>

            {/* REFERENCE MEDIA */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
              <div className="flex items-center gap-2 text-primaryColor font-bold text-sm border-b border-gray-100 pb-3">
                <FiImage size={18} /> Reference Media
              </div>

              {/* IMAGES */}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1">
                  <FiImage size={12} /> Images ({images.length})
                </p>
                {images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Reference ${idx + 1}`}
                        onClick={() => setActiveMedia({ type: "image", url: img })}
                        className="w-full h-20 object-cover rounded-xl border border-gray-200 cursor-pointer hover:opacity-90 transition"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No images uploaded.</p>
                )}
              </div>

              {/* VIDEOS */}
              <div className="pt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1">
                  <FiVideo size={12} /> Videos ({videos.length})
                </p>
                {videos.length > 0 ? (
                  <div className="space-y-2">
                    {videos.map((vid, idx) => (
                      <video
                        key={idx}
                        src={vid}
                        controls
                        className="w-full h-32 rounded-xl border border-gray-200 object-cover bg-black"
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No videos uploaded.</p>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* LIGHTBOX MODAL FOR IMAGES */}
        {activeMedia && activeMedia.type === "image" && (
          <div
            onClick={() => setActiveMedia(null)}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          >
            <div className="relative max-w-3xl max-h-[90vh]">
              <img
                src={activeMedia.url}
                alt="Enlarged reference"
                className="max-w-full max-h-[85vh] rounded-2xl object-contain"
              />
              <button
                onClick={() => setActiveMedia(null)}
                className="absolute -top-10 right-0 text-white font-bold text-sm bg-white/20 px-3 py-1 rounded-full"
              >
                ✕ Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierRQMDetails;
